import {
  ICandyRackItem,
  ICartItem,
  IProductCatalog,
  IRatePlan,
  ISpecialOffersConfig,
} from "@/common/api/unifiedPortal/interfaces";
import { processSubscriptionDataForDisplay } from "@/common/subscriptionHelper";
import {
  addOnLCNs,
  allstateLCNs,
  convertWebrootSku,
  webrootLicenseCategories,
  webrootOnlyAllstateSkus,
} from "@/common/webrootProductName";
import {
  FLAGS,
  MONTHS_IN_YEAR,
  SUBSCRIPTION_LAST_ALLSTATE_DAY,
  UNITED_STATES,
  UNITED_STATES_COUNTRY_CODE,
} from "@/define";
import { ExpirationState } from "@/globalEnums";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { useUserStore } from "@/stores/user";
import { sortArrayOfObjects } from "./commonFn";

export const config = [
  {
    licenseCategories: [webrootLicenseCategories.WE],
    icon: "webroot-icon",
  },
  {
    licenseCategories: [webrootLicenseCategories.WTP, webrootLicenseCategories.WTPF],
    icon: "webroot-total-protection",
  },
  {
    licenseCategories: [webrootLicenseCategories.WPCO],
    icon: "webroot-pc-optimizer",
  },
  {
    licenseCategories: [...addOnLCNs],
    icon: "icon-allstate",
  },
  {
    licenseCategories: [webrootLicenseCategories.WSVPN],
    icon: "webroot-secure-vpn",
  },
] as ISpecialOffersConfig[];

export function getCandyRackIcon(licenseCategory: string | undefined) {
  if (!licenseCategory) {
    return "webroot-icon";
  }

  return config.find(l => l.licenseCategories.some(i => i === licenseCategory))?.icon ?? "webroot-icon";
}

export function getSpecialOffersToDisplay(mainItem: ICartItem | undefined) {
  const buyFlowStore = useBuyFlowStore();
  const userStore = useUserStore();
  const subscriptionsStore = useSubscriptionsStore();
  let products: IProductCatalog[] = [];

  if (mainItem) {
    //Find all the candy rack items for the main item
    const candyRackDetailItems = buyFlowStore.plans.find(l => l.sku === mainItem?.sku)?.candyRackDetailItems;

    //if there is something in the candy rack that this user hasn't already selected for the cart
    if (candyRackDetailItems) {
      products = filterCandyRackDetailItems(mainItem, candyRackDetailItems);
    }
    //if we are logged in, then check to see if the user already has any of these items
    //and remove if the product catalog says to
    if (userStore.isEstablishedAccount) {
      const productsToRemove: IProductCatalog[] = [];
      const subscriptions = processSubscriptionDataForDisplay(subscriptionsStore.subscriptions);
      if (subscriptions && subscriptions.length > 0) {
        for (const product of products) {
          if (product.candyRackSettings?.hideIfOwned) {
            //the user could have more than one matching subscription, check them all
            const matches = subscriptions.filter(
              s => convertWebrootSku(s.sku ?? "", s.licenseCategoryName ?? "") === product.sku
            );
            for (const match of matches) {
              if (match.expiration !== ExpirationState.Purged) {
                productsToRemove.push(product);
                break;
              }
            }
          }
          if (product.sku === "WebrootSecureVPNP") {
            const filterSkuList = subscriptions.filter(
              s =>
                convertWebrootSku(s.sku ?? "", s.licenseCategoryName ?? "") === "WebrootTotalProtection" ||
                convertWebrootSku(s.sku ?? "", s.licenseCategoryName ?? "") === "WebrootTotalProtectionFamily"
            );
            for (const s of filterSkuList) {
              if (s.expiration !== ExpirationState.Purged) {
                if (!productsToRemove.includes(product)) {
                  productsToRemove.push(product);
                }
              }
            }
          }
        }
      }

      if (productsToRemove.length > 0) {
        // remove all matching skus and also skus sharing the same upgrade group/family
        products = products.filter(
          p =>
            !productsToRemove.some(
              pr =>
                pr.sku === p.sku ||
                (p.upgradeGroup && p.upgradeGroup === pr.upgradeGroup && p.candyRackSettings?.hideIfOwned)
            )
        );
      }

      if (
        subscriptions &&
        subscriptions.some(
          l => allstateLCNs.includes(l.licenseCategoryName ?? "") && l.remainingDays > SUBSCRIPTION_LAST_ALLSTATE_DAY
        )
      ) {
        products = products.filter(p => !webrootOnlyAllstateSkus.some(pr => pr === p.sku));
      }
    } else if (buyFlowStore.licenseKeyInfo?.candyRackDetailItems) {
      //OverWrite the candy rack items with the products from the API call
      products = filterCandyRackDetailItems(mainItem, buyFlowStore.licenseKeyInfo?.candyRackDetailItems);
    }
  }

  //Remove Allstate products if selected language is not English
  const selectedLanguage = userStore.selectedLanguage;
  if (selectedLanguage !== "en") {
    products = products.filter(p => !webrootOnlyAllstateSkus.some(pr => pr === p.sku));
  }

  //Remove Allstate products if the billing address country in the enhanced cart is not USA
  if (
    buyFlowStore.paymentFormAddress.country &&
    buyFlowStore.paymentFormAddress.country !== UNITED_STATES &&
    buyFlowStore.paymentFormAddress.country !== UNITED_STATES_COUNTRY_CODE
  ) {
    products = products.filter(p => !webrootOnlyAllstateSkus.some(pr => pr === p.sku));
  } else if (buyFlowStore.cart?.paymentMethodId && buyFlowStore.creditCards) {
    const selectedPayment = buyFlowStore.creditCards.find(l => l.id === buyFlowStore.cart?.paymentMethodId);
    if (
      selectedPayment?.address.country !== UNITED_STATES &&
      selectedPayment?.address.country !== UNITED_STATES_COUNTRY_CODE
    ) {
      products = products.filter(p => !webrootOnlyAllstateSkus.some(pr => pr === p.sku));
    }
  }

  return products;
}

function filterCandyRackDetailItems(mainItem: ICartItem, candyRackItems: ICandyRackItem[]): IProductCatalog[] {
  const buyFlowStore = useBuyFlowStore();
  //look up mainItem.ratePlanId in the product catalog
  const mainRatePlan = buyFlowStore
    .getRatePlansForSku(mainItem.sku, true)
    .find(l => l.ratePlanId === mainItem.ratePlanId);
  const products: IProductCatalog[] = [];

  if (!(FLAGS.ENABLE_CANDY_RACK_FOR_OPT_OUT_TRIAL === "true") && mainRatePlan?.isOptOutTrial) {
    // If the main rate plan is an opt-out trial, don't show candy rack items
    return [];
  }

  //filter each candy rack item that isn't already in the cart and sort it based on sequenceNumber
  const filteredCandyRackItems = candyRackItems
    .filter(l => !buyFlowStore.cart?.items.some(k => k.sku === l.sku))
    .sort((a, b) => (a?.sequenceNumber ? a.sequenceNumber : 0) - (b?.sequenceNumber ? b.sequenceNumber : 0));

  for (const item of filteredCandyRackItems) {
    //if the candy rack item applies to a specific seat count of the main item
    if (item.applyToIncludedUnits) {
      //exclude this if seat count does not match the item
      if (mainRatePlan?.includedUnits != item.applyToIncludedUnits) {
        continue;
      }
    }

    //if the candy rack item can be purchased and it is not one of those that should be bundled with the item
    const product = buyFlowStore.plans.find(
      l => l.sku === item.sku && !l.candyRackSettings?.bundleWithCart && l.ratePlans.some(k => k.canPurchaseOnline)
    );

    //add it to the candy rack list
    if (product) {
      products.push(product);
    }
  }

  return products;
}

function startingRatePlan(specialOfferItem: IProductCatalog) {
  const buyFlowStore = useBuyFlowStore();
  const mainItemSku = buyFlowStore.mainProductSku;
  const candyRackDetailItems = buyFlowStore.plans.find(l => l.sku === mainItemSku)?.candyRackDetailItems;
  const requiredUnits = candyRackDetailItems?.find(c => c.sku == specialOfferItem.sku)?.includedUnits;
  const ratePlan = specialOfferItem.ratePlans
    .filter(r => r.months == MONTHS_IN_YEAR)
    .sort((a, b) => {
      // Handle cases where includedUnits might be undefined
      const unitsA = a.includedUnits !== undefined ? a.includedUnits : Number.MAX_SAFE_INTEGER;
      const unitsB = b.includedUnits !== undefined ? b.includedUnits : Number.MAX_SAFE_INTEGER;
      return unitsA - unitsB;
    })[0];

  if (requiredUnits) {
    return specialOfferItem.ratePlans.find(r => r.includedUnits == requiredUnits) ?? ratePlan;
  } else {
    return ratePlan;
  }
}

export function bestMatchingCandyRackRatePlan(specialOfferItem: IProductCatalog, allowMultiYearPlans = false) {
  const buyFlowStore = useBuyFlowStore();
  const mainItem = buyFlowStore.plans
    .find(p => p.sku === buyFlowStore.mainProductSku)
    ?.ratePlans?.find(l => l.ratePlanId === buyFlowStore.selectedPlan?.ratePlanId);
  let ratePlan: IRatePlan;

  const mainItemProduct = buyFlowStore.plans.find(p => p.ratePlans.some(r => r.ratePlanId === mainItem?.ratePlanId));
  const mainItemRatePlan = mainItemProduct?.ratePlans?.find(l => l.ratePlanId === mainItem?.ratePlanId);
  if (mainItemRatePlan) {
    const candyRackItem = mainItemProduct?.candyRackDetailItems?.find(c => c.sku === specialOfferItem.sku);
    let itemRatePlans = buyFlowStore.getRatePlansForSku(specialOfferItem.sku);

    //if the candy rack says that we should assign a specific seat count version of the sku
    if (candyRackItem?.includedUnits) {
      //remove all the others from the list
      itemRatePlans = itemRatePlans.filter(
        p => (p.includedUnits ?? p.includedIdentities) === candyRackItem.includedUnits
      );
    } else {
      const tempRatePlans = itemRatePlans.filter(
        p =>
          (p.includedUnits ?? p.includedIdentities) ===
          (mainItemRatePlan.includedUnits ?? mainItemRatePlan.includedIdentities)
      );
      //if we found some with the right included units
      if (tempRatePlans.length > 0) {
        //that's the list for the next step
        itemRatePlans = tempRatePlans;
      } else if (mainItemRatePlan.includedUnits || mainItemRatePlan.includedIdentities) {
        //if the main item has an included units
        //find the nearest lesser units or if none, then the closest larger
        const unitCounts = itemRatePlans
          .map(p => (p.includedUnits ?? p.includedIdentities) || 0)
          .filter(q => q != 0)
          .sort((a, b) => a - b);
        //if there were any that had non-null included units
        if (unitCounts.length > 0) {
          let i = 0;
          while (unitCounts[i + 1] < (mainItemRatePlan.includedUnits ?? mainItemRatePlan.includedIdentities ?? 0)) {
            i++;
          }
          itemRatePlans = itemRatePlans.filter(p => (p.includedUnits ?? p.includedIdentities) === unitCounts[i]);
        }
      }
    }

    //Sort the remaining rate plans so the month are in order
    itemRatePlans = sortArrayOfObjects(itemRatePlans, "months", "ascending");
    if (allowMultiYearPlans) {
      //Try to find a rate plan that matches the main items term
      const matchedItemTerm = itemRatePlans.find(l => l.months === mainItemRatePlan.months);
      if (matchedItemTerm) {
        ratePlan = matchedItemTerm;
      } else {
        //If we can't match the term of the main product check for a shorter term
        const shorterItemTerm = itemRatePlans.find(l => l.months < mainItemRatePlan.months);
        if (shorterItemTerm) {
          ratePlan = shorterItemTerm;
        } else {
          //find the smallest plan term(for starting at $ ...)
          ratePlan = itemRatePlans[0];
        }
      }
    } else {
      ratePlan = itemRatePlans[0];
    }
    return ratePlan;
  }
  return startingRatePlan(specialOfferItem);
}
